export interface EatAttribution {
    authorName: string;
    authorRole: string;
    authorBio: string;
    authorPhoto: string;
    contributors: string;
    datePublished: string;
    dateModified: string;
    readMinutes: number | null;
}

export function emptyEatAttribution(): EatAttribution {
    return {
        authorName: '',
        authorRole: '',
        authorBio: '',
        authorPhoto: '',
        contributors: '',
        datePublished: '',
        dateModified: '',
        readMinutes: null,
    };
}

export function normalizeEatAttribution(value?: Partial<EatAttribution> | null): EatAttribution {
    const base = emptyEatAttribution();
    if (!value || typeof value !== 'object') {
        return base;
    }
    return {
        authorName: String(value.authorName || '').trim(),
        authorRole: String(value.authorRole || '').trim(),
        authorBio: String(value.authorBio || '').trim(),
        authorPhoto: '',
        contributors: String(value.contributors || '').trim(),
        datePublished: String(value.datePublished || '').trim(),
        dateModified: String(value.dateModified || '').trim(),
        readMinutes: parseReadMinutes(value.readMinutes),
    };
}

export function hasEatAuthor(value?: Partial<EatAttribution> | null): boolean {
    return !!(value && String(value.authorName || '').trim());
}

/**
 * Author E-E-A-T fields are scraped metadata the owner must complete.
 * Generate / polish / keywordSelection readOnly / articleContentReady must
 * not lock them. Share-preview visitors stay read-only.
 */
export function eatFieldsLocked(sharePreview: boolean): boolean {
    return !!sharePreview;
}

export function eatBioIsFilled(value?: Partial<EatAttribution> | null): boolean {
    return strip(value && value.authorBio).length >= 40;
}

export function eatDateIsFilled(value?: Partial<EatAttribution> | null): boolean {
    return !!(value && (String(value.datePublished || '').trim() || String(value.dateModified || '').trim()));
}

function strip(value: unknown): string {
    return String(value || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/<!--.*?-->/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function decode(value: string): string {
    return value
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#39;|&apos;/g, "'")
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)));
}

function parseReadMinutes(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
        return Math.round(value);
    }
    const match = String(value || '').match(/PT(\d+)M/i) || String(value || '').match(/(\d+)\s*(min|minute)/i);
    if (match) {
        return Number(match[1]);
    }
    const asNumber = Number(value);
    return Number.isFinite(asNumber) && asNumber > 0 ? Math.round(asNumber) : null;
}

function personName(value: unknown): string {
    if (!value) return '';
    if (typeof value === 'string') return strip(value);
    if (Array.isArray(value)) {
        return value.map(personName).filter(Boolean).join(', ');
    }
    if (typeof value === 'object') {
        const record = value as Record<string, unknown>;
        return strip(record.name || record.alternateName || '');
    }
    return '';
}

function personList(value: unknown): string[] {
    if (!value) return [];
    const items = Array.isArray(value) ? value : [value];
    return items.map(personName).filter(Boolean);
}

function firstRecord(value: unknown): Record<string, unknown> | null {
    if (!value || typeof value !== 'object') return null;
    const item = Array.isArray(value) ? value[0] : value;
    if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
    return item as Record<string, unknown>;
}

function collectJsonLd(html: string): unknown[] {
    const blocks = html.match(/<script[^>]*type=["']application\/ld\+json["'][^>]*>[\s\S]*?<\/script>/gi) || [];
    const out: unknown[] = [];
    blocks.forEach((block) => {
        const json = block.replace(/^<script[^>]*>/i, '').replace(/<\/script>$/i, '');
        try {
            const parsed = JSON.parse(json);
            if (Array.isArray(parsed)) {
                out.push(...parsed);
            } else {
                out.push(parsed);
            }
        } catch {
            // ignore broken JSON-LD
        }
    });
    return out;
}

function walkNodes(nodes: unknown[]): Record<string, unknown>[] {
    const found: Record<string, unknown>[] = [];
    const visit = (node: unknown) => {
        if (!node || typeof node !== 'object') return;
        if (Array.isArray(node)) {
            node.forEach(visit);
            return;
        }
        const record = node as Record<string, unknown>;
        found.push(record);
        if (record['@graph']) visit(record['@graph']);
    };
    nodes.forEach(visit);
    return found;
}

function isArticleType(type: unknown): boolean {
    const values = Array.isArray(type) ? type : [type];
    return values.some((item) => /article|blogposting|newsarticle|techreport/i.test(String(item || '')));
}

function attr(html: string, test: string): string {
    const match = html.match(new RegExp(`data-test=["']${test}["'][^>]*>([\\s\\S]*?)</`, 'i'));
    return match ? strip(decode(match[1])) : '';
}

function metaContent(html: string, names: string[]): string {
    for (const name of names) {
        const match = html.match(new RegExp(
            `<meta[^>]+(?:name|property)=["']${name}["'][^>]+content=["']([^"']+)["']`,
            'i'
        )) || html.match(new RegExp(
            `<meta[^>]+content=["']([^"']+)["'][^>]+(?:name|property)=["']${name}["']`,
            'i'
        ));
        if (match && match[1]) {
            return decode(match[1]).trim();
        }
    }
    return '';
}

function formatIsoDate(value: string): string {
    const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!iso) {
        return value;
    }
    const date = new Date(`${iso[1]}-${iso[2]}-${iso[3]}T00:00:00Z`);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    try {
        return new Intl.DateTimeFormat('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            timeZone: 'UTC',
        }).format(date);
    } catch {
        return `${Number(iso[3])}. ${Number(iso[2])}. ${iso[1]}`;
    }
}

function dateFromTimestamp(value: unknown): string {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 1000000000) {
        return '';
    }
    const ms = num < 1000000000000 ? num * 1000 : num;
    return formatIsoDate(new Date(ms).toISOString());
}

function fromNextData(html: string): Partial<EatAttribution> {
    const match = html.match(/<script[^>]*id=["']__NEXT_DATA__["'][^>]*>([\s\S]*?)<\/script>/i);
    if (!match) {
        return {};
    }
    try {
        const data = JSON.parse(match[1]);
        const page = data?.props?.pageProps?.page;
        if (!page || typeof page !== 'object') {
            return {};
        }
        const author = page.author || {};
        const contributors = Array.isArray(page.contributors)
            ? page.contributors.map((item: {name?: string}) => item && item.name).filter(Boolean).join(', ')
            : '';
        return {
            authorName: strip(author.name || ''),
            authorRole: strip(author.position || ''),
            authorBio: strip(author.shortBio || author.bio || ''),
            authorPhoto: '',
            contributors,
            datePublished: dateFromTimestamp(page.publishedAt) || dateFromTimestamp(page.editedAt),
            dateModified: dateFromTimestamp(page.editedAt),
            readMinutes: parseReadMinutes(page.timeToRead),
        };
    } catch {
        return {};
    }
}

function merge(base: EatAttribution, extra: Partial<EatAttribution>): EatAttribution {
    const next = {...base};
    (Object.keys(extra) as Array<keyof EatAttribution>).forEach((key) => {
        const value = extra[key];
        if (key === 'readMinutes') {
            if (next.readMinutes == null && parseReadMinutes(value) != null) {
                next.readMinutes = parseReadMinutes(value);
            }
            return;
        }
        if (!next[key] && typeof value === 'string' && value.trim()) {
            (next as unknown as Record<string, string>)[key] = value.trim();
        }
    });
    return next;
}

export function extractEatAttribution(html: string | null | undefined): EatAttribution {
    let result = emptyEatAttribution();
    if (!html) {
        return result;
    }

    const nodes = walkNodes(collectJsonLd(html));
    const articleNode = nodes.find((node) => isArticleType(node['@type'])) || nodes.find((node) => node.author);
    if (articleNode) {
        const authors = personList(articleNode.author);
        const firstAuthor = firstRecord(articleNode.author);
        result = merge(result, {
            authorName: authors[0] || '',
            contributors: authors.slice(1).join(', ') || personList(articleNode.contributor).join(', '),
            authorBio: strip(String(firstAuthor?.description || '')),
            authorRole: strip(String(firstAuthor?.jobTitle || '')),
            authorPhoto: '',
            datePublished: articleNode.datePublished ? formatIsoDate(String(articleNode.datePublished)) : '',
            dateModified: articleNode.dateModified ? formatIsoDate(String(articleNode.dateModified)) : '',
            readMinutes: parseReadMinutes(articleNode.timeRequired),
        });
        if (authors.length > 1 && !result.contributors) {
            result.contributors = authors.slice(1).join(', ');
        }
    }

    result = merge(result, fromNextData(html));

    const visibleDate = attr(html, 'date');
    const visibleRead = attr(html, 'time-to-read');
    const visibleContributors = attr(html, 'contributors').replace(/^contributors:\s*/i, '');
    result = merge(result, {
        datePublished: visibleDate,
        dateModified: visibleDate,
        readMinutes: parseReadMinutes(visibleRead),
        contributors: visibleContributors,
    });

    result = merge(result, {
        authorName: metaContent(html, ['author', 'article:author', 'og:article:author']),
        datePublished: metaContent(html, ['article:published_time', 'og:article:published_time', 'date']),
        dateModified: metaContent(html, ['article:modified_time', 'og:updated_time']),
    });

    const byline = html.match(/Author:\s*([^<]{3,80})/i);
    if (byline) {
        result = merge(result, {authorName: strip(decode(byline[1]))});
    }
    const contribLine = html.match(/Contributors?:\s*([^<]{3,160})/i);
    if (contribLine) {
        result = merge(result, {contributors: strip(decode(contribLine[1])).replace(/\s+and\s+/g, ', ')});
    }

    if (result.dateModified && !result.datePublished) {
        result.datePublished = result.dateModified;
    }
    if (result.authorName && result.contributors) {
        const names = result.contributors.split(/\s*,\s*|\s+and\s+/i).filter(Boolean);
        result.contributors = names.filter((name) => name.toLowerCase() !== result.authorName.toLowerCase()).join(', ');
    }
    return normalizeEatAttribution(result);
}
